// SuppliersList.js
import React, { useEffect, useMemo, useState } from "react"
import {
  Col,
  Row,
  Table,
  Avatar,
  Space,
  Typography,
  Descriptions,
  Tag,
  Input,
  Select,
  Button,
  Popconfirm,
  message,
} from "antd"
import PageHeader from "../../../components/Elements/PageHeader"
import { LiaShippingFastSolid } from "react-icons/lia"
import { collection, onSnapshot, query, orderBy, deleteDoc, doc } from "firebase/firestore"
import { db, storage } from "../../../firebase/config"
import CreateSupplierDrawer from "./CreateSupplierDrawer"
import EditSupplierDrawer from "./EditSupplierDrawer"

const { Text, Link } = Typography
const { Search } = Input

export default function SuppliersList() {
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(true)

  // filters
  const [qText, setQText] = useState("")
  const [countryFilter, setCountryFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")

  // edit
  const [editing, setEditing] = useState(null) // supplier object or null

  useEffect(() => {
    setLoading(true)
    const q = query(collection(db, "suppliers"), orderBy("company", "asc"))
    const unsub = onSnapshot(
      q,
      (snap) => {
        setSuppliers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        setLoading(false)
      },
      (e) => {
        console.error(e)
        setSuppliers([])
        setLoading(false)
      }
    )
    return () => unsub()
  }, [])

  const renderAddressShort = (r) => {
    const a = r.deliveryAddress || r.legalAddress || ""
    const city = r.deliveryCity || r.legalCity || r.city || ""
    const pc = r.deliveryPC || r.legalPC || r.postalCode || ""
    const country = r.country || ""
    return [a, city, pc, country].filter(Boolean).join(", ")
  }

  const filtered = useMemo(() => {
    const t = qText.trim().toLowerCase()
    return suppliers.filter((s) => {
      if (countryFilter !== "all" && (s.country || "Other") !== countryFilter) return false
      if (statusFilter !== "all") {
        const isActive = !(s.active === false)
        if ((statusFilter === "active" && !isActive) || (statusFilter === "inactive" && isActive)) {
          return false
        }
      }
      if (!t) return true
      const hay = [
        s.company,
        s.name,
        s.email,
        s.phone,
        s.vat,
        s.reg,
        s.city,
        s.deliveryCity,
        s.legalCity,
        s.accountNumber,
        s.bankName,
        s.bankCode,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return hay.includes(t)
    })
  }, [suppliers, qText, countryFilter, statusFilter])

  const handleDelete = async (record) => {
    try {
      await deleteDoc(doc(db, "suppliers", record.id))
      message.success("Supplier deleted.")
    } catch (e) {
      console.error(e)
      message.error("Failed to delete supplier.")
    }
  }

  const columns = [
    {
      title: "",
      dataIndex: "logoUrl",
      key: "logo",
      width: 56,
      render: (_, r) => (
        <Avatar size={40} src={r.logoUrl} style={{ background: "#1677ff" }}>
          {r.company?.[0]?.toUpperCase() || "S"}
        </Avatar>
      ),
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      render: (v) => <Text strong>{v || "-"}</Text>,
      sorter: (a, b) => (a.company || "").localeCompare(b.company || ""),
    },
    {
      title: "REG",
      dataIndex: "reg",
      key: "reg",
      render: (v) => v || "-",
      sorter: (a, b) => (a.reg || "").localeCompare(b.reg || ""),
      width: 160,
    },
    {
      title: "Address",
      key: "address",
      render: (_, r) => (
        <Text ellipsis style={{ maxWidth: 420 }}>
          {renderAddressShort(r) || "-"}
        </Text>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      width: 360,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{r.name || "-"}</Text>
          {r.email ? (
            <Link href={`mailto:${r.email}`}>{r.email}</Link>
          ) : (
            <Text type="secondary">no email</Text>
          )}
          {r.phone ? <Link href={`tel:${r.phone}`}>{r.phone}</Link> : null}
        </Space>
      ),
    },
    {
      title: "Status",
      key: "active",
      width: 110,
      render: (_, r) => (
        <Tag color={r.active === false ? "red" : "green"}>
          {r.active === false ? "Inactive" : "Active"}
        </Tag>
      ),
      filters: [
        { text: "Active", value: "active" },
        { text: "Inactive", value: "inactive" },
      ],
      onFilter: (val, rec) => (val === "active" ? !(rec.active === false) : rec.active === false),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, r) => (
        <Space>
          <Button type="link" onClick={() => setEditing(r)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete supplier?"
            description={`This will permanently delete ${r.company || "supplier"}.`}
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(r)}>
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const expandedRowRender = (r) => (
    <Descriptions
      bordered
      size="small"
      column={{ xs: 1, sm: 2, md: 2, lg: 3 }}
      labelStyle={{ width: 160 }}>
      <Descriptions.Item label="Company">{r.company || "-"}</Descriptions.Item>
      <Descriptions.Item label="Contact Person">{r.name || "-"}</Descriptions.Item>
      <Descriptions.Item label="Status">
        <Tag color={r.active === false ? "red" : "green"}>
          {r.active === false ? "Inactive" : "Active"}
        </Tag>
      </Descriptions.Item>

      <Descriptions.Item label="Email">
        {r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : "-"}
      </Descriptions.Item>
      <Descriptions.Item label="Phone">
        {r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : "-"}
      </Descriptions.Item>
      <Descriptions.Item label="Country">{r.country || "-"}</Descriptions.Item>

      <Descriptions.Item label="REG">{r.reg || "-"}</Descriptions.Item>
      <Descriptions.Item label="VAT">{r.vat || "-"}</Descriptions.Item>
      <Descriptions.Item label="Invoice Prefix">{r.invoicePrefix || "-"}</Descriptions.Item>

      <Descriptions.Item label="Legal Address">
        {[r.legalAddress, r.legalCity, r.legalPC].filter(Boolean).join(", ") || "-"}
      </Descriptions.Item>
      <Descriptions.Item label="Delivery Address" span={2}>
        {[r.deliveryAddress, r.deliveryCity, r.deliveryPC].filter(Boolean).join(", ") || "-"}
      </Descriptions.Item>

      <Descriptions.Item label="Bank Name">{r.bankName || "-"}</Descriptions.Item>
      <Descriptions.Item label="Bank Code">{r.bankCode || "-"}</Descriptions.Item>
      <Descriptions.Item label="IBAN">{r.accountNumber || "-"}</Descriptions.Item>

      <Descriptions.Item label="Logo URL" span={3}>
        {r.logoUrl ? (
          <a href={r.logoUrl} target="_blank" rel="noreferrer">
            {r.logoUrl}
          </a>
        ) : (
          "-"
        )}
      </Descriptions.Item>

      <Descriptions.Item label="Comment" span={3}>
        {r.comment || "-"}
      </Descriptions.Item>
    </Descriptions>
  )

  // Build Country options from data
  const countryOptions = useMemo(() => {
    const set = new Set(suppliers.map((s) => s.country || "Other"))
    return ["all", ...Array.from(set)]
  }, [suppliers])

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader text="Supplier Management" icon={<LiaShippingFastSolid />} />
        </Col>

        {/* Toolbar */}
        <Col span={24}>
          <Space wrap>
            <CreateSupplierDrawer dbInstance={db} storageInstance={storage} />
            <Search
              placeholder="Search company, REG, email, phone, city, VAT…"
              allowClear
              onChange={(e) => setQText(e.target.value)}
              onSearch={(val) => setQText(val)}
              style={{ width: 360 }}
            />
            <Select
              value={countryFilter}
              onChange={setCountryFilter}
              options={countryOptions.map((c) => ({
                value: c,
                label: c === "all" ? "All countries" : c,
              }))}
              style={{ width: 180 }}
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: "All statuses" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              style={{ width: 160 }}
            />
          </Space>
        </Col>

        <Col span={24}>
          <Table
            size="small"
            bordered
            loading={loading}
            dataSource={filtered}
            columns={columns}
            rowKey={(r) => r.id}
            pagination={{ defaultPageSize: 12, showSizeChanger: true }}
            expandable={{ expandedRowRender }}
          />
        </Col>
      </Row>

      {/* Edit Drawer */}
      <EditSupplierDrawer
        open={!!editing}
        supplier={editing}
        onClose={() => setEditing(null)}
        dbInstance={db}
      />
    </div>
  )
}
